import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { Component, OnInit, NgZone, OnDestroy, ChangeDetectorRef, HostListener } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { LoadingOverlayService, UserAuthService, UserRolesService } from '../../../shared/services';
import { INTERVAL_CHECK_PAD_DATA, INTERVAL_CHECK_PAD_INPROGRESS, TREATMENT_MONITOR_NUMBER } from '../../../shared/helpers/app.constants';
import { IntervalTimer, TimerState } from '../../../shared/helpers/intervalTimer';
import { Helper } from '../../../shared/helpers/helper';
import { PadService } from '../../../shared/services/pad.service';
import { finalize } from 'rxjs/operators';
import { IPadRealtimeInputData, PadRealtimeDataService } from '../pad-realtime-data.service';

@Component({
  selector: 'app-pad-realtime',
  templateUrl: './pad-realtime.component.html',
  styleUrls: ['./pad-realtime.component.scss']
})
export class PadRealtimeComponent implements OnInit, OnDestroy {
  subscriptions: Subscription[] = [];
  // padName
  isLoading: boolean;
  padName: string;
  companyId: string | number;

  dialogOpened: boolean = false;
  queryParams: any;
  currentTreatmentId = 0;
  treatmentInProgress: any;
  wellInProgress: any;

  intervalTimer: IntervalTimer;
  realtimeNoData: {
    lastTrue: number
    lastFalse: number
    countTrue: number
  };

  inputData: IPadRealtimeInputData | null;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private loadingOverlayService: LoadingOverlayService,
    private userAuthService: UserAuthService,
    private userRolesService: UserRolesService,
    private padService: PadService,
    private padRealtimeDataService: PadRealtimeDataService,
    private ngZone: NgZone
  ) {
    const navigation = this.router.getCurrentNavigation();
    this.inputData = navigation.extras.state as IPadRealtimeInputData | null;
    const userInfo = this.userAuthService.getUserInfo();
    const userRole = this.userRolesService.toUserRole(userInfo);
    this.companyId = userInfo.companyId || '';
  }

  ngOnInit(): void {
    this.loadingOverlayService.show();
    this.realtimeNoData = {
      lastTrue: new Date().getTime(),
      lastFalse: new Date().getTime(),
      countTrue: 0
    };

    this.subscriptions.push(
      this.route.params.subscribe((params: any) => {
        const padName = params.padName;
        if (typeof padName === 'string') {
          this.padName = padName;
          if (!this.inputData || !this.inputData.wellInProgress || !this.inputData.treatmentInProgress) {
            this.currentTreatmentId = 0;
            this.onLoadData(true);
          } else {
            this.setInputData(this.inputData);
          }
          this.loadingOverlayService.hide();
        } else {
          this.loadingOverlayService.hide();
        }
      })
    );

    this.runIntervalTimer();
  }

  ngOnDestroy(): void {
    this.onDestroyInterval();
    this.padRealtimeDataService.resetData();
    Helper.unsubscribe(this.subscriptions);
  }

  private setInputData(data: IPadRealtimeInputData): void {
    if (!data || !data.treatmentInProgress) {
      if (data) this.currentTreatmentId = data.currentTreatmentId;
      if (this.queryParams) {
        this.wellInProgress = null;
        this.treatmentInProgress = null;
        this.queryParams = null;
      }
    } else {
      this.wellInProgress = data.wellInProgress;
      this.treatmentInProgress = data.treatmentInProgress;
      this.currentTreatmentId = data.treatmentInProgress.id;
      // this.excludeTreatmentIds = data.excludeTreatmentIds;
      this.queryParams = {
        wellId: this.treatmentInProgress.wellId,
        treatmentId: this.treatmentInProgress.id,
        padName: this.padName
      };
    }
  }

  private onLoadData(isRefresh?: boolean): void {
    if (!this.padName) return;
    // reset counter for realtimeNoData.lastFalse
    this.realtimeNoData.lastFalse = new Date().getTime();
    if (!!isRefresh) this.loadingOverlayService.show();
    this.isLoading = true;

    this.padService.getPadByName(this.padName)
      .pipe(finalize(() => {
        this.isLoading = false;
        this.loadingOverlayService.hide();
      }))
      .subscribe(res => {
        const inputData = this.padRealtimeDataService.getLatestInProgress(res.result.wells, res.result, this.currentTreatmentId);
        setTimeout(() => { this.setInputData(inputData); });
      });
  }

  onTreatmentNoticeData(value: boolean) {
    const getTime = new Date().getTime();
    if (value) {
      this.realtimeNoData.lastTrue = getTime;
    } else {
      this.realtimeNoData.lastFalse = getTime;
    }
  }

  private runIntervalTimer(): void {
    this.ngZone.runOutsideAngular(() => {
      this.intervalTimer = new IntervalTimer(() => {
        // use 10% tolerant rate of INTERVAL_CHECK_PAD_DATA on compare time and INTERVAL_CHECK_PAD_INPROGRESS
        const thisTime = new Date().getTime();
        const compareTime = thisTime - this.realtimeNoData.lastFalse - INTERVAL_CHECK_PAD_INPROGRESS;
        const isNoData = (compareTime > - INTERVAL_CHECK_PAD_DATA / 10) && (!this.treatmentInProgress || this.realtimeNoData.lastTrue > this.realtimeNoData.lastFalse);
        if (!this.isLoading && isNoData) this.onLoadData();
      }, INTERVAL_CHECK_PAD_DATA);
    });
  }

  private onDestroyInterval(): void {
    if (this.intervalTimer) this.intervalTimer.stop();
  }

  onChangeTimerState(state: number): void {
    if (!this.dialogOpened && state == TimerState.resumed) {
      if (this.intervalTimer) this.intervalTimer.resume();
    } else if (state == TimerState.paused) {
      if (this.intervalTimer) this.intervalTimer.pause();
    }
  }

  @HostListener('window:visibilitychange', ['$event'])
  onVisibilityChange(event) {
    if (document.hidden === true && document.visibilityState === 'hidden') {
      this.onChangeTimerState(TimerState.paused);
    } else if (document.hidden === false && document.visibilityState === 'visible') {
      this.onChangeTimerState(TimerState.resumed);
    }
  }

}
